// @flow

import * as React from "react";

import classNames from "classnames";
import {
  append,
  contains,
  findIndex,
  identity,
  pipe,
  pick,
  prop,
  propEq,
  reject,
  subtract,
  uniq,
  union,
  without,
  partition,
  propSatisfies,
  includes,
} from "ramda";
import Downshift from "downshift";
import { useMount, useUpdateEffect } from "react-use";

import { SearchIcon } from "@quantum/ui";

import { nomeCorresponde } from "../../model";
import { useScrollArea } from "../comum/Scroll";

import "./MultiSelect.css";

const stateReducer = (state: any, changes: any): any => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.mouseUp:
      return {
        ...changes,
        inputValue: "",
        isOpen: state.isOpen,
      };

    case Downshift.stateChangeTypes.keyDownEnter:
      return {
        ...changes,
        inputValue: "",
        isOpen: state.isOpen,
        highlightedIndex: state.highlightedIndex,
      };

    case Downshift.stateChangeTypes.clickItem:
      return {
        ...changes,
        inputValue: state.inputValue,
        isOpen: state.isOpen,
        highlightedIndex: state.highlightedIndex,
      };

    case Downshift.stateChangeTypes.blurInput:
      return {
        ...changes,
        inputValue: "",
      };

    default:
      return changes;
  }
};

type Option = {
  id: string,
  value: string,
};

type OptionId = $PropertyType<Option, "id">;

type Props = {
  focused?: ?boolean,
  className?: ?string,
  disabled?: boolean,
  isOptionDisabled: (option: OptionId) => boolean,
  options: Array<Option>,
  value: Array<OptionId>,
  valueToScroll?: string | null,
  selectedOptionsFirst?: boolean,
  onChange: (Array<OptionId>) => mixed,
};

const takeOptionsBetween = (start, end, options) => {
  const startIndex = findIndex(propEq("id", start), options);
  const endIndex = findIndex(propEq("id", end), options);
  const [finalStart, finalEnd] = [startIndex, endIndex].sort(subtract);
  return options.slice(finalStart, finalEnd + 1);
};

const filterItem = (inputValue, value) =>
  !inputValue || nomeCorresponde(inputValue, value);

export const MultiSelect = ({
  focused = true,
  className,
  disabled,
  options,
  value = [],
  valueToScroll,
  selectedOptionsFirst = false,
  isOptionDisabled,
  onChange = identity,
}: Props) => {
  const inputRef = React.useRef();
  const holdingShift = React.useRef(false);
  const lastSelection = React.useRef(null);
  const pivotSelecion = React.useRef(null);
  const lastPivotChange = React.useRef([]);

  const focusInput = React.useCallback(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [inputRef]);

  const onChangeHandler = React.useCallback(
    (selection, { inputValue }) => {
      if (!selection) return;

      if (!holdingShift.current) pivotSelecion.current = null;

      if (pivotSelecion.current) {
        const filteredOptions = options.filter(option =>
          filterItem(inputValue, option.value)
        );
        const below = takeOptionsBetween(
          pivotSelecion.current,
          selection.id,
          filteredOptions
        );
        const above = takeOptionsBetween(
          selection.id,
          pivotSelecion.current,
          filteredOptions
        );
        const change = [...below, ...above].map(prop("id"));

        const withoutDisabled = reject(isOptionDisabled);
        const withoutPrevious = without(lastPivotChange.current);
        const withPivot = append(pivotSelecion.current);

        onChange(
          pipe(
            withoutPrevious,
            union(change),
            withPivot,
            uniq,
            withoutDisabled
          )(value)
        );
        lastPivotChange.current = change;
      } else if (contains(selection.id, value)) {
        onChange(without([selection.id], value));
        pivotSelecion.current = null;
        lastSelection.current = null;
        lastPivotChange.current = [];
      } else {
        onChange(append(selection.id, value));
        lastSelection.current = selection.id;
        lastPivotChange.current = [];
        if (pivotSelecion.current == null && holdingShift.current) {
          pivotSelecion.current = lastSelection.current;
        }
      }
      focusInput();
    },
    [
      isOptionDisabled,
      value,
      onChange,
      focusInput,
      holdingShift,
      lastSelection,
      lastPivotChange,
      options,
    ]
  );

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey) {
        holdingShift.current = true;
        pivotSelecion.current = lastSelection.current;
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (!event.shiftKey) {
        holdingShift.current = false;
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
    };
  }, [holdingShift, pivotSelecion, lastSelection]);

  const {
    registerScrollArea,
    registerScrollTarget,
    scrollTo,
  } = useScrollArea();

  useMount(() => {
    if (value.length > 0) {
      const target = options.find(option => value.includes(option.id));
      if (target) scrollTo(target.id, { offsetTop: -30 });
    }
  });

  useUpdateEffect(() => {
    if (valueToScroll) {
      const target = options.find(option => valueToScroll === option.id);
      if (target) scrollTo(target.id, { offsetTop: -30 });
    }
  });

  const preprocessedOptions = React.useMemo(
    () =>
      selectedOptionsFirst
        ? pipe(
            partition(
              propSatisfies(optionId => includes(optionId, value), "id")
            ),
            ([selectedOptions, otherOptions]) => [
              ...selectedOptions,
              ...otherOptions,
            ]
          )(options)
        : options,
    [options, value, selectedOptionsFirst]
  );

  return (
    <Downshift
      stateReducer={stateReducer}
      onChange={onChangeHandler}
      itemToString={item => (item ? String(item.id) : "")}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        inputValue,
        highlightedIndex,
        selectedItem,
      }) => (
        <div
          className={classNames("q-Select", "q-Select--multiple", className)}
        >
          <div className={classNames("q-SelectBody", "q-SelectBody--multiple")}>
            <div className="q-SelectBody__search">
              <SearchIcon />
              <input
                type="text"
                autoFocus={focused}
                ref={inputRef}
                {...getInputProps()}
              />
            </div>
            <ol
              className="q-SelectBody__items"
              {...getMenuProps({ ref: registerScrollArea })}
              onScroll={focusInput}
            >
              {preprocessedOptions
                .filter(item => filterItem(inputValue, item.value))
                .map((item, index) => (
                  <li
                    ref={registerScrollTarget(item.id)}
                    key={item.id}
                    className={classNames(
                      "q-SelectItem",
                      "q-SelectItem--multiple",
                      {
                        "q-SelectItem--selected": contains(item.id, value),
                        "q-SelectItem--disabled": isOptionDisabled(item.id),
                      }
                    )}
                    style={{
                      backgroundColor:
                        highlightedIndex === index ? "#f3f9ff" : "transparent",
                    }}
                  >
                    <label
                      {...pick(
                        ["onMouseMove"],
                        getItemProps({
                          index,
                          item,
                        })
                      )}
                    >
                      <input
                        type="checkbox"
                        {...getItemProps({
                          index,
                          item,
                          checked: contains(item.id, value),
                          disabled: isOptionDisabled(item.id),
                        })}
                        value={item.id}
                        onChange={identity}
                      />
                      {item.value}
                    </label>
                  </li>
                ))}
            </ol>
          </div>
        </div>
      )}
    </Downshift>
  );
};
